import { create } from 'zustand';

import {
  createShareLinkForcedThemeSlice,
  type ShareLinkForcedThemeSlice,
} from '~/components/v2/store/features/share-link-theme/slices/createShareLinkForcedThemeSlice';

/*
 * Upward channel from a mounted share-link surface to the app-root ThemeProvider. The writers
 * (the public collection and board surfaces, via `usePublicShareLinkForcedTheme`) sit far BELOW the reader
 * (`AppThemeProvider` wraps the whole tree in `_app`), so React context can't carry the value —
 * an external store is the only direction-agnostic transport.
 *
 * This store exists ONLY for `colorMode` data the server can never see — i.e. data that isn't in
 * the route's ISR pageProps, so the page's `getForcedTheme` static (the other feed into
 * `AppThemeProvider`) structurally cannot derive it:
 *   1. Passcode-protected links — `getStaticProps` runs with no visitor credentials, so their
 *      `presentationSettings` only exist client-side, after the visitor verifies.
 *   2. The editor's live preview — draft settings stream in over the localStorage channel and
 *      change as the user edits; they never touch any route props.
 * On a normal public link the write is redundant: the pageProps feed derives the same value, and
 * only that feed can cover the pre-hydration first paint (this store populates from a useEffect,
 * long after next-themes' inline script has already stamped the theme).
 *
 * Clears itself when the surface unmounts so the visitor's own theme snaps back. Deliberately NOT
 * persisted — forcing is a property of the mounted page, never of the browser.
 */
export const useShareLinkForcedThemeStore = create<ShareLinkForcedThemeSlice>()((...args) => ({
  ...createShareLinkForcedThemeSlice({ forcedTheme: undefined })(...args),
}));
